import { redirect, type LoaderFunctionArgs } from "react-router";
import { useAuthStore } from "@/stores/authStore";
import { useUiStore } from "@/stores/uiStore";
import { t } from "@/i18n/ko";
import { ensureBootstrapped } from "./bootstrap";
import { abandonOnboarding, ONBOARDING_ROUTES, onboardingStep } from "./onboarding";

const pathOf = (request: Request) => {
  const u = new URL(request.url);
  return `${u.pathname}${u.search}`;
};

/** 비회원이면 returnTo 저장 후 /login 페이지(AC0-94: 시트 없음). */
async function requireSession({ request }: LoaderFunctionArgs) {
  await ensureBootstrapped();
  if (useAuthStore.getState().status === "member") return null;
  useUiStore.getState().requestLogin({ returnTo: pathOf(request) });
  return redirect("/login");
}

/** 회원 전용. 세션이 없으면 /login. 온보딩 미완 세션으로 오면 온보딩 이탈이므로 로그아웃 후 /login(AC0-149). */
export async function requireMember({ request }: LoaderFunctionArgs) {
  await ensureBootstrapped();
  if (await abandonOnboarding()) useUiStore.getState().showSnackbar(t("onboarding.abandoned"));
  return requireSession({ request } as LoaderFunctionArgs);
}

/** 온보딩 미완 회원은 해당 단계로. 완료 회원이 온보딩 라우트에 오면 다음 단계 또는 returnTo/home. */
export async function requireOnboarding({ request }: LoaderFunctionArgs) {
  const guard = await requireSession({ request } as LoaderFunctionArgs);
  if (guard) return guard;
  const step = onboardingStep(useAuthStore.getState().user);
  const current = new URL(request.url).pathname;
  if (step && step !== current) return redirect(step);
  if (!step && (ONBOARDING_ROUTES as readonly string[]).includes(current)) {
    const returnTo = useUiStore.getState().returnTo;
    useUiStore.getState().setReturnTo(null);
    return redirect(returnTo ?? "/home");
  }
  return null;
}

/** 이미 회원이면 /login에 올 이유가 없다. */
export async function guestOnly() {
  await ensureBootstrapped();
  return useAuthStore.getState().status === "member" ? redirect("/home") : null;
}
